import { apiClient } from './client';

/** 서로 겹치지 않는 토큰 다섯 칸 + 토큰이 아닌 단위(크레딧·호출 수). */
export interface TokenTotals {
  input_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  units: number;
}

export interface CostGroup extends TokenTotals {
  key: string;
  calls: number;
  cost_usd: number;
}

export interface DailyCost {
  day: string;
  kind: string;
  calls: number;
  cost_usd: number;
}

export interface FreeTierUse {
  label: string;
  monthly_units: number;
  units_in_period: number;
  free_units_applied: number;
  discount_usd: number;
}

/** 채팅 턴 한 번(같은 request_id 의 모든 호출)에 든 평균 비용. */
export interface TurnCost {
  turns: number;
  total_usd: number;
  avg_usd: number;
  avg_chat_llm_usd: number;
  avg_tools_usd: number;
  avg_other_llm_usd: number;
}

export interface UsageSummary extends TokenTotals {
  start: string;
  end: string;
  calls: number;
  users: number;
  /** 사용자 한 명으로 좁힌 요약이면 그 id */
  user_id: string | null;
  cost_usd: number;
  billed_estimate_usd: number;
  free_tiers: FreeTierUse[];
  chat_turns: TurnCost | null;
  cache_hit_rate: number | null;
  unpriced_calls: number;
  unscoped_calls: number;
  usd_krw_rate: number;
  daily: DailyCost[];
  by_kind: CostGroup[];
  by_feature: CostGroup[];
  by_model: CostGroup[];
}

export type UserSort = 'cost' | 'calls' | 'tokens' | 'chat_turns' | 'last_at';

export interface UserUsage extends TokenTotals {
  /** null 이면 사용자 없는 호출(관리자 시험·unscoped) */
  user_id: string | null;
  name: string | null;
  tier_id: string | null;
  calls: number;
  cost_usd: number;
  total_tokens: number;
  chat_turns: number;
  cost_per_chat_turn_usd: number | null;
  last_at: string;
}

export interface UserUsageList {
  items: UserUsage[];
  total: number;
  usd_krw_rate: number;
}

export interface UsageEvent extends TokenTotals {
  id: string;
  occurred_at: string;
  user_id: string | null;
  user_name: string | null;
  thread_id: string | null;
  feature: string;
  kind: string;
  provider: string;
  model: string;
  trace_id: string | null;
  request_id: string | null;
  extra: Record<string, unknown>;
  cost_usd: number;
  priced: boolean;
}

export interface UsageEventList {
  items: UsageEvent[];
  total: number;
  usd_krw_rate: number;
  langfuse_project_id: string | null;
}

export interface TurnMessage {
  id: string;
  role: string;
  content: string;
  timestamp: string;
}

export interface UsageTurn {
  event_id: string;
  thread_id: string;
  user_message: TurnMessage | null;
  assistant_message: TurnMessage | null;
}

export interface UsagePrice {
  provider: string;
  model: string;
  meter: string;
  usd_per_unit: number;
  condition: Record<string, unknown> | null;
  valid_from: string;
  valid_until: string | null;
  source_url: string;
  checked_on: string;
  note: string | null;
}

export interface UsagePriceList {
  items: UsagePrice[];
  usd_krw_rate: number;
  notes: string[];
}

export interface UsagePeriod {
  start: string;
  end: string;
}

export interface UsageSummaryQuery extends UsagePeriod {
  /** 주면 그 사용자 한 명의 요약 ('none' 이면 사용자 없는 호출) */
  user_id?: string;
}

export interface UsageUserQuery extends UsagePeriod {
  q?: string;
  sort?: UserSort;
  desc?: boolean;
  limit?: number;
  offset?: number;
}

export interface UsageEventQuery extends UsagePeriod {
  /** 'none' 이면 사용자 없는 호출만 */
  user_id?: string;
  feature?: string;
  kind?: string;
  model?: string;
  thread_id?: string;
  request_id?: string;
  limit?: number;
  offset?: number;
}

/** 호출 탭 조건 — 요약·사용자 탭에서 행을 누르면 호출 탭이 이 조건으로 열린다. */
export type UsageEventFilters = Omit<UsageEventQuery, 'start' | 'end' | 'limit' | 'offset'>;

// 빈 칸은 보내지 않는다 — 서버가 빈 문자열을 조건으로 받아 아무것도 안 나온다.
const compact = (query: object) =>
  Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== ''));

export const usageApi = {
  summary: async (query: UsageSummaryQuery): Promise<UsageSummary> =>
    (await apiClient.get<UsageSummary>('/usage/summary', { params: compact(query) })).data,

  users: async (query: UsageUserQuery): Promise<UserUsageList> =>
    (await apiClient.get<UserUsageList>('/usage/users', { params: compact(query) })).data,

  events: async (query: UsageEventQuery): Promise<UsageEventList> =>
    (await apiClient.get<UsageEventList>('/usage/events', { params: compact(query) })).data,

  turn: async (eventId: string): Promise<UsageTurn> =>
    (await apiClient.get<UsageTurn>(`/usage/events/${eventId}/turn`)).data,

  prices: async (): Promise<UsagePriceList> => (await apiClient.get<UsagePriceList>('/usage/prices')).data,
};
